import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShiftCard } from "@/components/ShiftCard";
import { ArrowRight, CalendarClock, Inbox, Sparkles, Zap } from "lucide-react";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const Stat = ({ label, value, testId }) => (
  <div className="card-surface p-5" data-testid={testId}>
    <p className="overline">{label}</p>
    <p className="mt-2 font-display text-3xl font-semibold text-slate-900">{value}</p>
  </div>
);

export default function WorkerHome() {
  const { user } = useAuth();
  const [ops, setOps] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    const [o, r, m] = await Promise.all([
      api.get("/worker/opportunities"),
      api.get("/worker/requests"),
      api.get("/worker/shifts"),
    ]);
    setOps(o.data);
    setReqs(r.data);
    setMine(m.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const confirmed = mine.filter((s) => s.status === "Confirmed");
  const urgent = ops.filter((s) => s.urgent);

  return (
    <div data-testid="worker-home">
      <p className="overline">{greeting()}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        {user?.name?.split(" ")[0]}, here is what fits you
      </h1>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Matching shifts" value={ops.length} testId="stat-matching" />
        <Stat label="Urgent nearby" value={urgent.length} testId="stat-urgent" />
        <Stat label="Pending requests" value={reqs.length} testId="stat-requests" />
        <Stat label="Confirmed" value={confirmed.length} testId="stat-confirmed" />
      </div>

      {reqs.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-accentx" />
            <h2 className="font-display text-2xl font-semibold tracking-tight">Coverage requests waiting on you</h2>
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {reqs.map((s) => (
              <ShiftCard key={s.shift_id} shift={s} to={`/worker/shifts/${s.shift_id}`} actionLabel="Review request" />
            ))}
          </div>
        </section>
      )}

      {urgent.length > 0 && (
        <section className="mt-10" data-testid="urgent-section">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-accentx" />
            <h2 className="font-display text-2xl font-semibold tracking-tight">Urgent coverage near you</h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">Needed today or tomorrow — these fill fast.</p>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {urgent.map((s) => (
              <ShiftCard key={s.shift_id} shift={s} to={`/worker/shifts/${s.shift_id}`} actionLabel="View & claim" />
            ))}
          </div>
        </section>
      )}

      <section className="mt-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" />
            <h2 className="font-display text-2xl font-semibold tracking-tight">Opportunities matching you</h2>
          </div>
          <Link to="/worker/opportunities" className="btn-ghost" data-testid="see-all-opportunities">
            See all & filter <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading opportunities…</p>
        ) : ops.length === 0 ? (
          <div className="card-surface mt-6 p-8">
            <p className="text-sm text-slate-600">
              No matching shifts yet. Add availability and preferred ZIPs to see more.
            </p>
            <Link to="/worker/availability" className="btn-primary mt-5" data-testid="empty-set-availability">
              Set availability
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {ops.slice(0, 6).map((s) => (
              <ShiftCard key={s.shift_id} shift={s} to={`/worker/shifts/${s.shift_id}`} />
            ))}
          </div>
        )}
      </section>

      {confirmed.length > 0 && (
        <section className="mt-12">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-emerald-600" />
            <h2 className="font-display text-2xl font-semibold tracking-tight">My upcoming confirmed shifts</h2>
          </div>
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {confirmed.map((s) => (
              <ShiftCard key={s.shift_id} shift={s} to={`/worker/shifts/${s.shift_id}`} showMatch={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
